import * as React from 'react';
import ActionStatus from '@sunbit-dev/common/enums/ActionStatus';
import sunbitApi from '../services/SunbitApi';
import ErrorReducer from './errorReducer';
import Verticals from '@sunbit-dev/common/enums/Verticals';
import MerchantType from '@sunbit-dev/common/enums/MerchantType';
import Originators from '@sunbit-dev/common/enums/Originators';
import Origin from '@sunbit-dev/common/enums/Origin';
import { StateCodes } from '@sunbit-dev/common/enums/State';
import wizardSteps, { Pages } from '../constants/wizardSteps';
import { AppContextState } from './models';

export const reducer = (state: any, action: any) => {
  if (action.status === ActionStatus.Failure) {
    state = {
      ...state,
      error: ErrorReducer(state.error, action),
    };
  }
  switch (action.type) {
    case 'setIsExtraVerificationNeeded':
      return {
        ...state,
        appData: {
          ...state.appData,
          isExtraVerificationNeeded: action.payload,
        },
      };
    case 'retailerData':
      return {
        ...state,
        retailer: action.payload,
      };
    case 'switchPage':
      return {
        ...state,
        appData: {
          ...state.appData,
          page: action.payload.page,
          pageName: action.payload.pageName,
        },
      };
    case 'activeApplication':
      return {
        ...state,
        appData: {
          ...state.appData,
          isActiveApplication: action.payload,
        },
      };
    case 'setPageName':
      return {
        ...state,
        appData: action.payload,
      };
    case 'resetAppData':
      return {
        ...state,
        appData: initialState.appData,
      };
    case 'setDownPayments':
      return {
        ...state,
        downPayments: action.payload,
      };
    case 'setInitialDownPayments':
      return {
        ...state,
        initialDownPayments: action.payload,
      };
    case 'setIsPs1Associated':
      return {
        ...state,
        isPs1Associated: action.payload,
      };
    case 'setFeatureFlag':
      return {
        ...state,
        analytics: {
          ...state.analytics,
          isFeatureFlagEnabled: action.payload,
        },
      };
    case 'setAnalyticsUser':
      return {
        ...state,
        analytics: {
          ...state.analytics,
          userIdForAnalytics: action.payload,
        },
      };
    default:
      return state;
  }
};

const initialState: AppContextState = {
  error: {
    isOpen: false,
    title: '',
    description: '',
    button: '',
    submit: () => {},
  },
  initialDownPayments: [0, 0, 0],
  downPayments: [0, 0, 0],
  retailer: {
    amountRequested: 0,
    applicationAmountFinancedLowerLimit: 0,
    applicationAmountLowerLimit: 0,
    applicationAmountUpperLimit: 0,
    merchantAddress: {
      address: '',
      city: '',
      state: StateCodes.CA,
      zipcode: '',
    },
    merchantAprLowerLimit: 0,
    merchantAprUpperLimit: 0,
    merchantId: 0,
    merchantName: '',
    merchantOriginator: Originators.Tab,
    merchantOriginatorActiveStates: [],
    merchantType: MerchantType.RETAILER,
    merchantVertical: Verticals.Car_Dealerships,
    onlineLogoUrl: '',
    origin: Origin.ONLINE_APPLICATION,
    subUrlName: '',
    chainFeatures: {},
  },
  isPs1Associated: 0,
  appData: {
    page: wizardSteps['EnterPhoneNumber'],
    pageName: Pages.EnterPhoneNumber,
    isActiveApplication: false,
    isExtraVerificationNeeded: false,
  },
  analytics: {
    userIdForAnalytics: Math.floor(Math.random() * 999999) + 100000,
    isFeatureFlagEnabled: false,
  },
  getRetailerData: () => {},
  prevPageContext: () => {},
  setNextPage: () => {},
  setPageName: () => {},
  setIsActiveApplication: () => {},
  resetAppData: () => {},
  setDownPayments: () => {},
  setInitialDownPayments: () => {},
  setFeatureFlag: () => {},
  setAnalyticsUser: () => {},
  setIsPs1Associated: () => {},
  setIsExtraVerificationNeeded: () => {},
};

export const AppContext = React.createContext<AppContextState>(initialState);

export const AppContextProvider = (props: any) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const setIsExtraVerificationNeeded = (isExtraVerificationNeeded: boolean) => {
    dispatch({
      type: 'setIsExtraVerificationNeeded',
      payload: isExtraVerificationNeeded,
    });
  };

  const getRetailerData = async () => {
    try {
      const retailerData = await sunbitApi.getRetailerData();

      dispatch({
        type: 'retailerData',
        payload: retailerData,
      });
    } catch (errorPayload) {
      throw errorPayload;
    }
  };

  const setNextPage = (page: Pages) => {
    dispatch({
      type: 'switchPage',
      payload: { page: wizardSteps[page], pageName: page },
    });
  };

  const setIsActiveApplication = (isActiveApplication: boolean) => {
    dispatch({
      type: 'activeApplication',
      payload: isActiveApplication,
    });
  };

  const resetAppData = () => {
    dispatch({
      type: 'resetAppData',
    });
  };

  const setPageName = (page: Pages) => {
    const data = { ...state.appData };
    data.pageName = page;
    dispatch({
      type: 'setPageName',
      payload: data,
    });
  };

  const setDownPayments = (newDownPayments: Array<number>) => {
    dispatch({
      type: 'setDownPayments',
      payload: newDownPayments,
    });
  };

  const setInitialDownPayments = (newDownPayments: Array<number>) => {
    dispatch({
      type: 'setInitialDownPayments',
      payload: newDownPayments,
    });
  };

  const setFeatureFlag = (isActive: boolean) => {
    dispatch({
      type: 'setFeatureFlag',
      payload: isActive,
    });
  };

  const setIsPs1Associated = (isAssociated: boolean) => {
    dispatch({
      type: 'setIsPs1Associated',
      payload: isAssociated,
    });
  };

  const setAnalyticsUser = (userId: number) => {
    dispatch({
      type: 'setAnalyticsUser',
      payload: userId,
    });
  };

  return (
    <AppContext.Provider
      value={{
        ...state,
        setIsExtraVerificationNeeded,
        setNextPage,
        getRetailerData,
        setIsActiveApplication,
        setPageName,
        resetAppData,
        setDownPayments,
        setInitialDownPayments,
        setFeatureFlag,
        setAnalyticsUser,
        setIsPs1Associated,
      }}>
      {props.children}
    </AppContext.Provider>
  );
};
